import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DeviceIdService } from '@shared/services.device-id';
import { LoadingService } from '@shared/services.loading';
import { EncryptionService } from '@shared/services/encryption.service';
import { MsisdnStorageService } from '@shared/services/msisdn-storage.service';
import { TokenService } from '@shared/services/token.service';
import { KeyStorage } from '@shared/utils.constant';
import { Observable, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RestApiService {
  private urlParams: Record<string, any> | null = null;
  private readonly destroyRef = inject(DestroyRef);
  private readonly msisdnStorage = inject(MsisdnStorageService);

  /**
   * Payload for the `x-url-payload` header: current URL query params plus the
   * msisdn, which now lives in storage rather than in the URL (URL value wins if both exist).
   */
  get listOfParams(): string {
    const msisdn = this.msisdnStorage.get();
    // An msisdn explicitly present in the URL (deep link) wins over the stored one.
    const merged = { msisdn: msisdn ?? undefined, ...this.urlParams };
    return this.urlParams || msisdn ? JSON.stringify(merged) : '';
  }

  constructor(
    private loadingService: LoadingService,
    private httpClient: HttpClient,
    private tokenService: TokenService,
    private deviceIdService: DeviceIdService,
    private encryptionService: EncryptionService,
    private translateService: TranslateService,
    activatedRoute: ActivatedRoute
  ) {
    activatedRoute.queryParams?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: any) => this.urlParams = params ?? null);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.loadingService.decrease();
    const errorMessage = error.error?.message || error.message || error.toString();
    return throwError(() => new Error(errorMessage));
  }

  getRequestWithoutDevice(apiUrl: string, params: any) {
    this.loadingService.increase();

    const headers = new HttpHeaders({ 'Content-type': 'application/json' });

    return this.httpClient.get<any>(apiUrl, { params, headers }).pipe(tap({
      next: (res) => {
        this.loadingService.decrease();
        return res;
      },
      error: (error) => {
        this.handleError(error);
      }
    })
    );
  }

  getRequest(apiUrl: string, params: any, additionalHeader?: any) {
    this.loadingService.increase();

    const token = this.tokenService.getToken();
    const storeData = this.tokenService.getData('store-data');
    const tokenPoint = this.tokenService.getData(KeyStorage.tokenPoint);
    const channelId = additionalHeader && additionalHeader[0].key === 'CHANNELID';

    let headers;
    if (channelId) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'x-transaction-id-package': additionalHeader,
        'CHANNELID': additionalHeader[0].value,
        'Content-type': 'application/json'
      });
    } else if (token && additionalHeader) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'x-transaction-id-package': additionalHeader,
        'Content-type': 'application/json',
        'AccessToken': token
      });
    } else if (token &&
      (!apiUrl.includes('/api/store/widget/abroad') ||
        !apiUrl.includes('/api/store-page/widget/roaming-usage'))
    ) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'Content-type': 'application/json',
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'AccessToken': token
      });
    } else if (storeData && (apiUrl.includes('/api/store/widget') ||
      apiUrl.includes('/api/store/menu') ||
      apiUrl.includes('/api/store/product') ||
      apiUrl.includes('/api/store/widget/abroad') ||
      apiUrl.includes('/api/subscriber/info/details') ||
      apiUrl.includes('/api/store-page/widget/roaming-usage') ||
      apiUrl.includes('/api/bill-now') ||
      apiUrl.includes('/api/order/bill-history') ||
      apiUrl.includes('/api/fmc/v1/track-order') ||
      apiUrl.includes('/api/fmc/v1/customer-profile') ||
      apiUrl.includes('/api/v1/votes/result') ||
      apiUrl.includes('v1/wec-auction/categories/state') ||
      apiUrl.includes('v1/wec-auction/categories/history')
    )) {
      const xToken = JSON.parse(storeData).x_token;

      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'Content-type': 'application/json',
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'X-TOKEN': xToken
      });
    } else if (additionalHeader) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'x-transaction-id-package': additionalHeader,
        'Content-type': 'application/json'
      });
    } else if (tokenPoint) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'Content-type': 'application/json',
        'X-TOKEN': tokenPoint
      });
    } else {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'Content-type': 'application/json'
      });
    }

    // Add Accept-Language header
    headers = headers.set('Accept-Language', this.translateService.currentLang || 'id');
    return this.httpClient.get<any>(apiUrl, { params, headers }).pipe(tap({
      next: (res) => {
        this.loadingService.decrease();
        return res;
      },
      error: (error) => {
        this.handleError(error);
      }
    })
    );
  }

  postRequest(apiUrl: string, body: any, additionalHeader?: any) {
    this.loadingService.increase();

    const token = this.tokenService.getToken();
    const storeData = this.tokenService.getData('store-data');
    const tokenPoint = this.tokenService.getData(KeyStorage.tokenPoint);
    const channelId = additionalHeader && additionalHeader?.[0]?.key === 'CHANNELID';

    let headers: HttpHeaders;
    if (channelId) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'x-transaction-id-package': additionalHeader,
        'CHANNELID': additionalHeader[0].value,
        'Content-type': 'application/json'
      });
    } else if (token && additionalHeader) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'x-transaction-id-package': additionalHeader,
        'x-transaction-type': apiUrl.search('/api/package') !== -1 ? 'DP' : '',
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'Content-type': 'application/json',
        'AccessToken': token
      });
    } else if (additionalHeader) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'x-transaction-type': apiUrl.search('/api/package') !== -1 ? 'DP' : '',
        'x-transaction-id-package': additionalHeader,
        'Content-type': 'application/json'
      });
    } else if (storeData &&
      (apiUrl.includes('/subscriber/info/total-usage') ||
        apiUrl.includes('/api/store/widget') ||
        apiUrl.includes('/api/store/menu') ||
        apiUrl.includes('/api/store/product') ||
        apiUrl.includes('/api/subscriber/info/details') ||
        apiUrl.includes('/api/subscriber/info/v3/details-quota') ||
        apiUrl.includes('/api/magic-button') ||
        apiUrl.includes('/api/submit-consent') ||
        apiUrl.includes('api/store/product/detail') ||
        apiUrl.includes('/api/bill-domestic') ||
        apiUrl.includes('/api/bill-roaming') ||
        apiUrl.includes('/api/bill-history') ||
        apiUrl.includes('/api/order/bill-history') ||
        apiUrl.includes('/api/payment-onelink') ||
        apiUrl.includes('/api/fmc/v1/submit-order') ||
        apiUrl.includes('/api/fmc/v2/submit-order') ||
        apiUrl.includes('/api/payment-onelink') ||
        apiUrl.includes('/api/v1/votes/submit') ||
        apiUrl.includes('/api/v1/devbun/payments') ||
        apiUrl.includes('/api/v1/wec-auction/bidders/bid/initial') ||
        apiUrl.includes('/api/v1/wec-auction/bidders/bid/add')
      )) {
      const xToken = JSON.parse(storeData).x_token;
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'Content-type': 'application/json',
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'X-TOKEN': xToken
      });
    } else if (token) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'Content-type': 'application/json',
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'AccessToken': token
      });
    } else if (tokenPoint) {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'Content-type': 'application/json',
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams),
        'X-TOKEN': tokenPoint
      });
    } else {
      headers = new HttpHeaders({
        'device-id': this.deviceIdService.getDeviceId(),
        'Content-type': 'application/json',
        'x-url-payload': this.encryptionService.newEncryptData(this.listOfParams)
      });
    }

    if (additionalHeader) {
      if (typeof additionalHeader === 'object' && !Array.isArray(additionalHeader)) {
        Object.keys(additionalHeader).forEach(key => {
          headers = headers.set(key, additionalHeader[key]);
        });
      }

      else if (Array.isArray(additionalHeader)) {
        additionalHeader.forEach(item => {
          if (item.key && item.value) {
            headers = headers.set(item.key, item.value);
          }
        });
      }
    }

    // Add Accept-Language header
    headers = headers.set('Accept-Language', this.translateService.currentLang || 'id');

    return this.httpClient.post<any>(apiUrl, body, { headers }).pipe(tap({
      next: (res) => {
        this.loadingService.decrease();
        return res.data;
      },
      error: (error) => {
        this.handleError(error);
      }
    })
    );
  }
}
